import { useState } from "react";
import { useCfmContext } from "../../../Context/CfmContext";
import InputSmall from "../../Inputs/InputSmall";
import RemoveIcon from "../../Buttons/RemoveIcon";
import AddIcon from "../../Buttons/AddIcon";
import DisplayMedium from "../../Inputs/DisplayMedium";
import InputDate from "../../Inputs/InputDate";
import "../../../../../../css/CashflowModeller2/Tabs/CashLumpSum.css";
import "../../../../../../css/CashflowModeller2/DialogBoxes/DialogBoxes.css";
import getPensionOptions from "../../../Utils/PensionOptions";
import PensionCalculations from "../../../Context/HelperFunctions/PensionCalculations";
import TFCOptions from "../../../Utils/TFCOptions";
import Input2Medium from "../../Inputs/Input2Medium";
import Input1Big from "../../Inputs/Input1Big";
import { getDobByName, getAgeOn } from "../../../Utils/GetDobByName";

const CashLumpSum = () => {
  const { state, dispatch } = useCfmContext();
  const drawdowns = state.drawdowns || [];
  const pensionResults = PensionCalculations();

  const [tfcData, setTfcData] = useState({ name: "", age: "", percentage: "" });
  const [phaseInput, setPhaseInput] = useState({
    start: "",
    end: "",
    amount: "",
  });

  // errors.add = strings/messages
  // errors.rows = booleans only
  const [errors, setErrors] = useState({ add: {}, rows: {} });

  const setAddError = (key, val) =>
    setErrors((prev) => ({ ...prev, add: { ...prev.add, [key]: val } }));

  const setRowError = (rowIdx, key, val) =>
    setErrors((prev) => ({
      ...prev,
      rows: {
        ...(prev.rows || {}),
        [rowIdx]: { ...(prev.rows?.[rowIdx] || {}), [key]: !!val },
      },
    }));

  const setPhaseError = (rowIdx, phaseIdx, key, val) =>
    setErrors((prev) => ({
      ...prev,
      rows: {
        ...(prev.rows || {}),
        [rowIdx]: {
          ...(prev.rows?.[rowIdx] || {}),
          phases: {
            ...(prev.rows?.[rowIdx]?.phases || {}),
            [phaseIdx]: {
              ...(prev.rows?.[rowIdx]?.phases?.[phaseIdx] || {}),
              [key]: !!val,
            },
          },
        },
      },
    }));

  const tfcOptions = TFCOptions(state);
  const isMaxTfcEntered = parseFloat(tfcData.percentage) >= 25;

  const handleChange = (setter) => (field, value) => {
    setter((prev) => ({ ...prev, [field]: value }));
    setAddError(field, false);
  };

  const isValidMMYYYY = (value) => /^(0[1-9]|1[0-2])\/\d{4}$/.test(value);

  const getTfcAmount = (name, age, type) => {
    const entry = pensionResults.find(
      (res) => res.name === name && res.type === type
    );
    const raw =
      entry?.pensionFunds?.find((row) => row.age === parseInt(age))
        ?.tflsWithdrawal || "";
    return typeof raw === "string" ? raw.slice(1) : String(raw).slice(1);
  };

  const handleAdd = () => {
    const { name, age, percentage } = tfcData;
    const { start, end, amount } = phaseInput;
    const [fullName, typePart] = (name || "").split(" - ").map((s) => s.trim());

    if (!fullName || !typePart) {
      setAddError("name", "Source is required");
      return;
    }

    const hasValidAgeAndPercentage =
      age.trim() !== "" && percentage.trim() !== "";
    const hasPhasedFields = !!(start && end && amount);
    const isValid = name && (hasValidAgeAndPercentage || hasPhasedFields);

    if (!isValid) {
      if (!name) setAddError("name", "Source is required");
      if (hasValidAgeAndPercentage && !age)
        setAddError("age", "Age is required");
      if (hasValidAgeAndPercentage && !percentage)
        setAddError("percentage", "Percentage is required");
      if (!hasValidAgeAndPercentage && !hasPhasedFields && !start)
        setAddError("start", "Start date is required");
      if (!hasValidAgeAndPercentage && !hasPhasedFields && !end)
        setAddError("end", "End date is required");
      if (!hasValidAgeAndPercentage && !hasPhasedFields && !amount)
        setAddError("amount", "Amount is required");
      return;
    }

    if (hasValidAgeAndPercentage) {
      if (parseInt(age, 10) < 55) {
        setAddError("age", "Minimum age for TFLS is 55");
        return;
      }
      if (parseFloat(percentage) > 25) {
        setAddError("percentage", "TFLS cannot exceed 25%");
        return;
      }
    }

    if (hasPhasedFields) {
      if (!isValidMMYYYY(start)) {
        setAddError("start", "Date must be in MM/YYYY format");
        return;
      }
      if (!isValidMMYYYY(end)) {
        setAddError("end", "Date must be in MM/YYYY format");
        return;
      }

      const dob = getDobByName(state, fullName);
      const startAge = getAgeOn(dob, start);
      const endAge = getAgeOn(dob, end);

      if (Number.isFinite(startAge) && startAge < 55) {
        setAddError("start", "Minimum age for TFLS is 55");
        return;
      }
      if (Number.isFinite(endAge) && endAge < startAge) {
        setAddError("end", "End must be after Start");
        return;
      }

      if (parseFloat(amount) > 268275) {
        setAddError("amount", "Phased TFLS amount cannot exceed £268,275.");
        return;
      }
    }

    const newEntry = {
      name: fullName,
      type: typePart,
      age: hasValidAgeAndPercentage ? age : "",
      percentage: hasValidAgeAndPercentage ? percentage : "",
      phases: hasPhasedFields ? [{ start, end, amount }] : [],
    };

    dispatch({ type: "UPDATE_DRAWDOWNS", payload: [...drawdowns, newEntry] });
    setTfcData({ name: "", age: "", percentage: "" });
    setPhaseInput({ start: "", end: "", amount: "" });
    setErrors((prev) => ({ ...prev, add: {} })); // clear add form errors
  };

  const handleRemove = (idx) => {
    const updated = drawdowns.filter((_, i) => i !== idx);
    dispatch({ type: "UPDATE_DRAWDOWNS", payload: updated });
    setErrors((prev) => {
      const oldRows = prev.rows || {};
      const reindexed = {};
      updated.forEach((_, newI) => {
        const oldI = newI >= idx ? newI + 1 : newI;
        if (oldRows[oldI]) reindexed[newI] = oldRows[oldI];
      });
      return { ...prev, rows: reindexed };
    });
  };

  return (
    <div className="CashLumpSumContainer">
      {/* Add form */}
      <div className="CashLumpSumRow">
        <div className="CashLumpSumColumn">
          <div className="Title">Cash Lump Sum</div>
          <div className="CashLumpSumRow">
            <Input2Medium
              label="Source"
              value={tfcData.name}
              onChange={(val) => handleChange(setTfcData)("name", val)}
              options={tfcOptions}
              isError={!!errors.add?.name}
              isDisabled={tfcOptions.length === 0}
              type="invType"
              placeholderText="William"
              isDropdownCenter
            />
            <InputSmall
              label="Age"
              value={tfcData.age}
              onChange={(val) => handleChange(setTfcData)("age", val)}
              isError={!!errors.add?.age}
              isDisabled={tfcOptions.length === 0}
            />
            <InputSmall
              label="%"
              value={tfcData.percentage}
              type="number"
              onChange={(val) => handleChange(setTfcData)("percentage", val)}
              isError={!!errors.add?.percentage}
              isDisabled={tfcOptions.length === 0}
            />
            <DisplayMedium
              label="TFC Amount"
              currency
              value=""
              type="number"
              readOnly
            />
          </div>
        </div>

        <div className="CashLumpSumColumn">
          <div className="Title">Phased Tax Free Cash</div>
          <div className="CashLumpSumRow">
            <InputDate
              label="Start"
              value={phaseInput.start}
              onChange={(val) => handleChange(setPhaseInput)("start", val)}
              isError={!!errors.add?.start}
              isDisabled={tfcOptions.length === 0 || isMaxTfcEntered}
              dateFormat="MM/YYYY"
            />
            <InputDate
              label="End"
              value={phaseInput.end}
              onChange={(val) => handleChange(setPhaseInput)("end", val)}
              isError={!!errors.add?.end}
              isDisabled={tfcOptions.length === 0 || isMaxTfcEntered}
              dateFormat="MM/YYYY"
            />
            <Input1Big
              label="Amount"
              value={phaseInput.amount}
              type="number"
              currency
              onChange={(val) => handleChange(setPhaseInput)("amount", val)}
              isError={!!errors.add?.amount}
              isDisabled={tfcOptions.length === 0 || isMaxTfcEntered}
            />
            <AddIcon onClick={handleAdd} />
          </div>
        </div>
      </div>

      {/* Add form messages */}
      {typeof errors.add?.percentage === "string" && (
        <div className="error Label right-align">{errors.add.percentage}</div>
      )}
      {typeof errors.add?.amount === "string" && (
        <div className="error Label right-align">{errors.add.amount}</div>
      )}
      {(typeof errors.add?.start === "string" ||
        typeof errors.add?.end === "string") && (
        <div className="error Label right-align">
          {errors.add.start || errors.add.end}
        </div>
      )}
      {typeof errors.add?.age === "string" && (
        <div className="error Label right-align">{errors.add.age}</div>
      )}

      {/* Existing rows */}
      {drawdowns.map((entry, index) => (
        <div key={index} className="CashLumpSumRow">
          <div className="CashLumpSumColumn">
            <div />
            <div className="CashLumpSumRow">
              <Input2Medium
                value={`${entry.name} - ${entry.type}`}
                readOnly
                type="invType"
              />
              <InputSmall
                value={entry.age}
                onChange={(val) => {
                  const ageNum = parseInt(val || "0", 10);
                  setRowError(
                    index,
                    "age",
                    Number.isFinite(ageNum) && ageNum < 55
                  );
                  const updated = [...drawdowns];
                  updated[index] = { ...updated[index], age: val };
                  dispatch({ type: "UPDATE_DRAWDOWNS", payload: updated });
                }}
                isError={!!errors.rows?.[index]?.age}
              />
              <InputSmall
                value={entry.percentage}
                onChange={(val) => {
                  const cappedVal = parseFloat(val) > 25 ? "25" : val;
                  const updated = [...drawdowns];
                  updated[index] = { ...updated[index], percentage: cappedVal };

                  const totalTfc = drawdowns.reduce((sum, d, idx) => {
                    const tfcAtAge =
                      parseFloat(getTfcAmount(d.name, d.age, d.type)) || 0;
                    const pct =
                      idx === index
                        ? parseFloat(cappedVal || 0) || 0
                        : parseFloat(d.percentage || 0) || 0;
                    sum += tfcAtAge * (pct / 100);
                    if (Array.isArray(d.phases)) {
                      sum += d.phases.reduce(
                        (acc, p) => acc + (parseFloat(p.amount || 0) || 0),
                        0
                      );
                    }
                    return sum;
                  }, 0);

                  setRowError(
                    index,
                    "percentage",
                    parseFloat(val) > 25 || totalTfc > 268275
                  );
                  dispatch({ type: "UPDATE_DRAWDOWNS", payload: updated });
                }}
                isError={!!errors.rows?.[index]?.percentage}
              />
              <DisplayMedium
                value={getTfcAmount(entry.name, entry.age, entry.type)}
                readOnly
                currency
                type="number"
                isDisabled={entry.age === "" && entry.percentage === ""}
              />
            </div>
          </div>

          <div className="CashLumpSumColumn">
            {(entry.phases?.length > 0
              ? entry.phases
              : [{ start: "", end: "", amount: "" }]
            ).map((phase, i) => (
              <div key={i} className="CashLumpSumRow">
                <InputDate
                  value={phase.start}
                  onChange={(val) => {
                    if (val && !isValidMMYYYY(val)) {
                      setPhaseError(index, i, "start", true);
                      return;
                    }
                    const dob = getDobByName(state, entry.name);
                    const startAge = getAgeOn(dob, val);
                    setPhaseError(
                      index,
                      i,
                      "start",
                      Number.isFinite(startAge) && startAge < 55
                    );
                    const updated = [...drawdowns];
                    if (!updated[index].phases) updated[index].phases = [];
                    if (!updated[index].phases[i])
                      updated[index].phases[i] = {
                        start: "",
                        end: "",
                        amount: "",
                      };
                    updated[index].phases[i].start = val;
                    dispatch({ type: "UPDATE_DRAWDOWNS", payload: updated });
                  }}
                  dateFormat="MM/YYYY"
                  isError={!!errors.rows?.[index]?.phases?.[i]?.start}
                  isDisabled={
                    entry.percentage >= 25 ||
                    getTfcAmount(entry.name, entry.age, entry.type) ===
                      "268275.00"
                  }
                />
                <InputDate
                  value={phase.end}
                  onChange={(val) => {
                    if (val && !isValidMMYYYY(val)) {
                      setPhaseError(index, i, "end", true);
                      return;
                    }
                    const dob = getDobByName(state, entry.name);
                    const endAge = getAgeOn(dob, val);
                    setPhaseError(
                      index,
                      i,
                      "end",
                      Number.isFinite(endAge) && endAge < 55
                    );
                    const updated = [...drawdowns];
                    if (!updated[index].phases) updated[index].phases = [];
                    if (!updated[index].phases[i])
                      updated[index].phases[i] = {
                        start: "",
                        end: "",
                        amount: "",
                      };
                    updated[index].phases[i].end = val;
                    dispatch({ type: "UPDATE_DRAWDOWNS", payload: updated });
                  }}
                  dateFormat="MM/YYYY"
                  isDisabled={
                    entry.percentage >= 25 ||
                    getTfcAmount(entry.name, entry.age, entry.type) ===
                      "268275.00"
                  }
                  isError={!!errors.rows?.[index]?.phases?.[i]?.end}
                />
                <Input1Big
                  value={phase.amount}
                  currency
                  type="number"
                  isDisabled={
                    entry.percentage >= 25 ||
                    getTfcAmount(entry.name, entry.age, entry.type) ===
                      "268275.00"
                  }
                  onChange={(val) => {
                    const newAmount = parseFloat(val) || 0;
                    const totalTfc = drawdowns.reduce((sum, d, idx) => {
                      const tfcAtAge =
                        parseFloat(getTfcAmount(d.name, d.age, d.type)) || 0;
                      sum +=
                        tfcAtAge * ((parseFloat(d.percentage || 0) || 0) / 100);
                      d.phases?.forEach((p, j) => {
                        sum +=
                          idx === index && j === i
                            ? newAmount
                            : parseFloat(p.amount || 0) || 0;
                      });
                      return sum;
                    }, 0);
                    setPhaseError(index, i, "amount", totalTfc > 268275);
                    const updated = [...drawdowns];
                    if (!updated[index].phases) updated[index].phases = [];
                    if (!updated[index].phases[i])
                      updated[index].phases[i] = {
                        start: "",
                        end: "",
                        amount: "",
                      };
                    updated[index].phases[i].amount = val;
                    dispatch({ type: "UPDATE_DRAWDOWNS", payload: updated });
                  }}
                  isError={!!errors.rows?.[index]?.phases?.[i]?.amount}
                />
                <RemoveIcon onClick={() => handleRemove(index)} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default CashLumpSum;
